/**
 * Product Tour Hook
 * Manages the product tour state and provides methods to control it
 */

import { useEffect, useCallback, useState, useRef } from 'react';
import { driver, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { tourSteps, TOUR_STORAGE_KEY } from './TourConfig';
import './ProductTour.css';

export function useProductTour() {
  const [hasSeenTour, setHasSeenTour] = useState<boolean>(() => {
    return localStorage.getItem(TOUR_STORAGE_KEY) === 'true';
  });
  const [isActive, setIsActive] = useState(false);
  const driverRef = useRef<Driver | null>(null);
  const hasStartedRef = useRef(false); // Prevent multiple starts

  // Initialize driver instance
  useEffect(() => {
    // Helper to mark tour as completed
    const markTourComplete = () => {
      localStorage.setItem(TOUR_STORAGE_KEY, 'true');
      setHasSeenTour(true);
      setIsActive(false);
    };

    driverRef.current = driver({
      showProgress: true,
      animate: true,
      smoothScroll: true,
      stagePadding: 10,
      stageRadius: 8,
      popoverClass: 'driver-popover-theme',
      overlayColor: 'rgba(0, 0, 0, 0.7)',
      nextBtnText: 'ಮುಂದೆ →',
      prevBtnText: '← ಹಿಂದೆ',
      doneBtnText: 'ಮುಗಿಸಿ ✓',
      showButtons: ['next', 'previous', 'close'],
      steps: tourSteps,
      // Called when close button (X) is clicked
      onCloseClick: () => {
        markTourComplete();
        driverRef.current?.destroy();
      },
      // Called when user completes the tour (last step done button)
      onDestroyStarted: () => {
        markTourComplete();
      },
      // Called after tour is destroyed (backup save)
      onDestroyed: () => {
        // Ensure completion is saved even if other callbacks missed
        localStorage.setItem(TOUR_STORAGE_KEY, 'true');
        setHasSeenTour(true);
        setIsActive(false);
      },
    });

    return () => {
      driverRef.current?.destroy();
    };
  }, []);

  const startTour = useCallback(() => {
    // Prevent starting multiple times
    if (hasStartedRef.current || isActive) {
      return;
    }

    // Check localStorage again as a double-check
    if (localStorage.getItem(TOUR_STORAGE_KEY) === 'true') {
      setHasSeenTour(true);
      return;
    }

    if (driverRef.current) {
      // Filter steps based on DOM presence
      const validSteps = tourSteps.filter(step => {
        if (!step.element) return true; // Welcome modal has no element
        const el = document.querySelector(step.element as string);
        return el !== null;
      });

      // Reconfigure with valid steps only
      driverRef.current.setConfig({ steps: validSteps });

      // IMPORTANT: Mark tour as seen BEFORE starting
      // This ensures it won't show again even if callbacks fail
      localStorage.setItem(TOUR_STORAGE_KEY, 'true');
      setHasSeenTour(true);

      driverRef.current.drive();
      setIsActive(true);
      hasStartedRef.current = true; // Mark as started
    }
  }, [isActive]);

  const resetTour = useCallback(() => {
    localStorage.removeItem(TOUR_STORAGE_KEY);
    setHasSeenTour(false);
    hasStartedRef.current = false; // Allow starting again
  }, []);

  const stopTour = useCallback(() => {
    driverRef.current?.destroy();
    setIsActive(false);
  }, []);

  return {
    hasSeenTour,
    isActive,
    startTour,
    resetTour,
    stopTour,
  };
}

export default useProductTour;
